import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";

export default class extends Controller {
  static values = {
    url: String,
    pageSize: { type: Number, default: 50 },
    create: Boolean,
    // When true on a multi-select, suppress `change` while the dropdown is
    // open and fire a single `change` on dropdown close. Callers on filter
    // bars (e.g. `change->turbo-filter#filter`) opt in so a user can pick
    // several values without the page navigating after the first click.
    batchChangesUntilClose: Boolean,
  };

  connect() {
    this.offset = 0;
    this.hasMore = true;
    this.loading = false;
    this.initTomSelect();
    this.setupBatchChangesUntilClose();
  }

  disconnect() {
    if (this.captureChangeHandler) {
      this.element.removeEventListener("change", this.captureChangeHandler, true);
    }
    if (this.select) this.select.destroy();
  }

  setupBatchChangesUntilClose() {
    if (!this.batchChangesUntilCloseValue) return;
    if (!this.element.multiple) return;

    this.dropdownOpen = false;
    this.hadChangeWhileOpen = false;

    this.captureChangeHandler = (event) => {
      if (this.dropdownOpen) {
        event.stopImmediatePropagation();
        this.hadChangeWhileOpen = true;
      }
    };
    this.element.addEventListener("change", this.captureChangeHandler, true);

    this.select.on("dropdown_open", () => {
      this.dropdownOpen = true;
      this.hadChangeWhileOpen = false;
    });

    this.select.on("dropdown_close", () => {
      this.dropdownOpen = false;
      if (this.hadChangeWhileOpen) {
        this.hadChangeWhileOpen = false;
        this.element.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  initTomSelect() {
    const options = this.buildOptions();

    if (this.hasUrlValue) {
      options.load = (query, callback) => this.load(query, callback);
    }

    this.select = new TomSelect(this.element, options);

    if (this.element.disabled) {
      this.select.disable();
    }

    // TomSelect hides the original <select>, so its autofocus attribute no
    // longer takes effect (e.g. the opportunity inline field editor).
    if (this.element.autofocus) {
      this.select.focus();
    }

    // Single-select keeps focus on the control <input> after a choice, which
    // leaves a blinking caret beside the selected value (unlike Select2).
    // Blur so the control settles as a closed select; multi-select keeps focus
    // so the user can add another item without reopening.
    if (!this.element.multiple) {
      this.select.on("item_add", () => this.select.blur());
      this.holdValueWhileSearching();
    }

    if (this.hasUrlValue) {
      this.select.on("focus", () => this.loadOnFocus());
      this.select.on("dropdown_open", () => this.bindScroll());
      this.select.on("dropdown_close", () => this.unbindScroll());
      this.select.on("type", () => this.resetPagination());
    }
  }

  // The stylesheet greys the selected .item while a single-select has focus,
  // so the field still shows what is held rather than looking wiped (see
  // components/tom_select.scss). Once there IS a query that hint only crowds
  // it, so flag the wrapper and let the CSS drop it. Deliberately not
  // control_input.placeholder: Tom Select owns that and clears it whenever
  // the select has items.
  holdValueWhileSearching() {
    const typing = (active) => this.select.wrapper.classList.toggle("ts-typing", active);

    this.select.on("type", (query) => typing(Boolean(query)));
    this.select.on("blur", () => typing(false));
    this.select.on("change", () => typing(false));
    this.select.on("dropdown_close", () => typing(false));
  }

  buildOptions() {
    const hasBlankOption = this.element.querySelector('option[value=""]') !== null;
    const plugins = {};

    if (hasBlankOption && !this.element.multiple) {
      plugins.clear_button = { title: "Clear" };
    }

    if (this.element.multiple) {
      plugins.remove_button = { title: "Remove" };
    }

    return {
      placeholder: this.element.getAttribute("placeholder"),
      allowEmptyOption: hasBlankOption,
      create: this.createValue,
      maxItems: this.element.multiple ? null : 1,
      maxOptions: null,
      hideSelected: true,
      clearAfterSelect: true,
      plugins,
    };
  }

  // -- Remote fetch --

  load(query, callback) {
    this.resetPagination();
    this.fetch(query)
      .then((data) => callback(data))
      .catch(() => callback([]));
  }

  loadOnFocus() {
    if (!this.select.control_input.value.length) {
      this.select.load("");
    }
  }

  // -- Infinite scroll --

  bindScroll() {
    this.scrollTarget = this.select.dropdown_content;
    this.scrollHandler = this.onScroll.bind(this);
    this.scrollTarget.addEventListener("scroll", this.scrollHandler);
  }

  unbindScroll() {
    if (this.scrollTarget && this.scrollHandler) {
      this.scrollTarget.removeEventListener("scroll", this.scrollHandler);
      this.scrollTarget = null;
      this.scrollHandler = null;
    }
  }

  onScroll() {
    if (this.loading || !this.hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = this.scrollTarget;
    if (scrollTop + clientHeight < scrollHeight - 50) return;

    this.loadMore();
  }

  loadMore() {
    this.loading = true;
    const query = this.select.inputValue();

    this.fetch(query)
      .then((data) => {
        data.forEach((item) => this.select.addOption(item));
        this.select.refreshOptions(false);
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
      });
  }

  // -- Shared --

  fetch(query) {
    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set("q", query);
    url.searchParams.set("limit", this.pageSizeValue);
    url.searchParams.set("offset", this.offset);

    return fetch(url)
      .then((response) => response.json())
      .then((data) => {
        this.hasMore = data.length === this.pageSizeValue;
        this.offset += data.length;
        return data.map(([text, value]) => ({ text, value }));
      });
  }

  resetPagination() {
    this.offset = 0;
    this.hasMore = true;
  }
}
